import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  PaymentCheckoutContentType,
  PaymentCheckoutWizardService,
} from '@app/account/shared/payment-checkout-wizard/payment-checkout-wizard.service';
import { ChangableComponent } from '@app/models/changable.component';
import {
  PaymentConfiguration,
  PaymentOptions,
  PaymentWidgetService,
} from '@app/shared/checkout-wizards/components/payment-widget';
import { logger } from '@core/helpers/logger';
import {
  AccountService,
  BillingService,
  CollectionsService,
  DeviceOrientationEnum,
  EventsSequenceExtended,
  EventsSequenceExtendedService,
  FilesService,
  ProfileService,
  ScheduleService,
  ScreenService,
  UserServiceContractsService,
} from '@core/services';
import { WebsocketService } from '@core/websocket';
import { IEntityUpdate } from '@models/lingo2-models/messaging';
import { Store } from '@ngrx/store';
import { captureException } from '@sentry/angular';
import { getProfileSettings } from '@store/reducers/data.reducer';
import { getMyCurrency } from '@store/reducers/profile.reducer';
import { addMinutes, addMonths, startOfWeek } from 'date-fns';
import { WebEvent } from 'lingo2-api-models';
import {
  BillingOperation,
  Collection,
  CollectionLicenseEnum,
  CreateUserServiceContractRequest,
  CReceipt,
  CSchool,
  CurrencyEnum,
  EUserServiceType,
  EventsSequence,
  IBillingCard,
  IExchangeRate,
  IHash,
  IImageFile,
  ImageSizeEnum,
  IUserAccess,
  Meeting,
  MeetingTypeEnum,
  SignupUserServiceContractRequest,
  SignupUserServiceContractResponse,
  SingleEvent,
  User,
  UserProfile,
  UserSegmentEnum,
  UserService,
  UserServiceContract,
  UserServiceContractIntervalEnum,
  UserServiceContractor,
  UserServiceContractOrder,
  UserServiceScheduleRequest,
  UserServiceScheduleResponse,
} from 'lingo2-models';
import { FormatPipe } from 'lingo2-ngx-date-fns';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ToastrService } from 'ngx-toastr';
import { OnUiButtonState, OnUiCover } from 'onclass-ui';
import { filter, mergeAll, Observable, of, Subscription, takeUntil, zip } from 'rxjs';
import { debounceTime, first, switchMap, tap } from 'rxjs/operators';

interface AuthorPaymentType {
  fullName: string;
  userPic: string;
  statsRating: number;
  statsMeetCount: number;
  participants: number;
}

interface ContentPaymentType {
  coverUrl: string;
  multiCover: any;
  title: string;
  type?: string;
  subjectTitle?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  lessons?: number;
  isSingle?: boolean;
  meetings_limit: number;
  age_from: number;
  age_to: number;
  level: string;
}

interface ISignupEvent {
  begin_at: Date;
  end_at: Date;
}

@Component({
  selector: 'app-payment-checkout-wizard',
  templateUrl: './payment-checkout-wizard.component.html',
  styleUrls: ['./payment-checkout-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentCheckoutWizardComponent extends ChangableComponent implements OnInit, OnDestroy {
  private screenService = inject(ScreenService);
  private billingService = inject(BillingService);
  private collectionsService = inject(CollectionsService);
  private profileService = inject(ProfileService);
  private scheduleService = inject(ScheduleService);
  private eventsSequenceExtendedService = inject(EventsSequenceExtendedService);
  private userServiceContractsService = inject(UserServiceContractsService);
  private paymentWidgetService = inject(PaymentWidgetService);
  private store = inject(Store);
  private toastr = inject(ToastrService);
  private formatPipe = inject(FormatPipe);
  private router = inject(Router);
  models = inject(PaymentCheckoutWizardService);
  deviceService = inject(DeviceDetectorService);
  protected websocketService = inject(WebsocketService);

  @Input()
  public set user(value: User) {
    this.models.init(
      {
        teacher: value,
      },
      true,
    );
    this.loadProfile();
  }

  @Input()
  public set content(value: Meeting | UserService | Collection) {
    if (!value) {
      this.models.init(
        {
          content: new UserService(),
        },
        true,
      );
      return;
    }

    if (value instanceof Meeting) {
      if (value?.can?.open || value?.can?.autoOpen) {
        this.models.goNextStep('payment');
        return;
      }
    }
    this.models.init(
      {
        content: value,
      },
      this.models.content?.id !== value.id,
    );
    if (!this.models.teacher) {
      this.models.init(
        {
          teacher: value?.author,
        },
        this.models.teacher?.id !== value.id,
      );
    }
    if (value instanceof UserService) {
      if (value.school) {
        this.prepareSchoolCard(value.school);
      } else {
        if (value.author) {
          this.prepareUserCard(value.author);
        }
      }
    } else {
      if (value.author) {
        this.prepareUserCard(value.author);
      }
    }
    this.prepareContentCard(value);
    // this.loadUserCards();
    this.detectChanges();
  }

  @Input()
  public set time(value: Date) {
    this.models.init(
      {
        time: value,
      },
      true,
    );
  }

  @Input()
  public set me(me: User) {
    const oldMeId = this._me?.id;
    this._me = me;
    /* this.initScheduleRefresh(this._me?.id, oldMeId); */
  }

  public get me() {
    return this._me;
  }

  @Output() public succeed = new EventEmitter<boolean>();
  @Output() public closed = new EventEmitter<boolean>();
  @Output() public stateChanged = new EventEmitter<string>();
  @Output() public userContentFullInModal = new EventEmitter<UserService>();

  public isSafari = this.screenService.isSafari;

  public contentData: ContentPaymentType = {
    coverUrl: '',
    multiCover: null,
    title: '',
    subjectTitle: '',
    startDate: null,
    endDate: null,
    lessons: 0,
    isSingle: null,
    meetings_limit: null,
    age_from: null,
    age_to: null,
    level: null,
  };
  public authorData: AuthorPaymentType = {
    fullName: '',
    userPic: '',
    statsRating: null,
    statsMeetCount: null,
    participants: null,
  };
  public teacherProfile: Partial<UserProfile>;
  public _me: User;
  public selectedLicense: CollectionLicenseEnum;

  public cardsState: boolean = null;
  public paymentProcessing = false;
  public payButtonsState: OnUiButtonState = 'default';
  public paymentProcessingError: string = null;
  public signupProcessingError: string = null;
  private _resetPayButtonState: any;
  public signupProcessing = false;
  public isOwnServiceError = false;

  public _billingCard: IBillingCard;
  public paymentFormValid = true;
  public receipt: CReceipt;
  public totalFormatted = '--';
  public myCurrency: CurrencyEnum;
  public exchangeRate: IExchangeRate;
  public isAgreementError = true;
  public showAgreementError: boolean;
  public courses: EventsSequence[];
  public loadingCourses: boolean;
  public errorMessage: string;

  public get billingOperation() {
    return this._billingOperation;
  }

  private _billingOperation: BillingOperation;
  private responseTimer: Date;
  public userServiceContractOrder: UserServiceContractOrder;
  public userServiceContract: UserServiceContract;
  public userServiceContractor: UserServiceContractor;
  private watchForContractOrderPaid$$: Subscription;

  private _isCheckout = false;
  public paymentOptions: PaymentOptions;
  private _userSettings: Partial<IUserAccess>;
  public paymentConfigured = false;
  public payClicked = false;
  public openAddAccountModal = false;
  public addAccountProviderId: string;
  public afterAddAccountNeedUpdate: number;

  public ngOnInit() {
    this.suggestLicense();

    of(...[this.watchWsReconnected$, this.watchScheduleUpdated$])
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SOCKET-ERROR');
        },
      });

    this.models.state$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (step) => {
        switch (step) {
          case 'detail':
            this.loadProfile();
            break;
          case 'datetime':
            this.prepareContentCard(this.models.content);
            // this.loadUserCards();
            this.refreshSchedule();
            break;
        }

        if (this.models.time) {
          this.models.addTime(this.models.time);
        }

        this.stateChanged.emit(step);
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    this.refreshSchedule();

    this.store
      .select(getMyCurrency)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (currency) => {
          this.myCurrency = currency;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.resetForm();
    this.models.clearData();
  }

  /** PREPARE CONTENT **/

  protected prepareUserCard(user: User) {
    this.authorData.fullName = AccountService.getUserFullName(user) || null;
    this.authorData.userPic =
      FilesService.getFileUrlBySize(user.userpic_id, ImageSizeEnum.md) || '/assets/onclass-logo.svg';
    if (user?.stats) {
      this.authorData.statsRating = user?.stats?.reviews_rating;
      this.authorData.statsMeetCount = user?.stats?.reviews_count;
    } else {
      this.authorData.statsRating = null;
      this.authorData.statsMeetCount = null;
    }
  }

  protected prepareSchoolCard(school: CSchool) {
    this.authorData.fullName = school.title || null;
    if (school?.cover) {
      this.authorData.userPic = school?.cover?.smX2?.url || school?.cover?.md?.url || '/assets/onclass-logo.svg';
    } else {
      this.contentData.coverUrl = '/assets/onclass-logo.svg';
    }
    if (school?.participants.length > 0) {
      this.authorData.participants = school?.participants.length;
    } else {
      this.authorData.participants = null;
    }
  }

  protected prepareContentCard(content: Meeting | UserService | Collection) {
    this.contentData.title = content?.title;
    this.contentData.subjectTitle = content?.subject.title;

    if (content instanceof Meeting || content instanceof UserService) {
      this.contentData.coverUrl = content?.cover?.md?.url;
    } else if (content instanceof Collection) {
      if (content?.cover) {
        this.contentData.coverUrl = content?.cover?.md?.url;
      } else if (content?.multi_cover_id) {
        this.contentData.multiCover = content?.multi_cover_id.map((id) =>
          FilesService.getFileUrlBySize(id, ImageSizeEnum.md),
        );
      }
    }

    if (content instanceof Meeting) {
      this.contentData.startDate = content?.begin_at;
      this.contentData.endDate = content?.end_at;
      this.contentData.type = this.typeLabel(content?.type, 'meeting');
    }

    if (content instanceof UserService) {
      this.contentData.isSingle = !content?.is?.group;
      this.contentData.meetings_limit = content?.options?.meetings_limit;
      this.contentData.level = content?.level?.code;
      this.contentData.age_from = content?.options?.age_from;
      this.contentData.age_to = content?.options?.age_to;
      this.contentData.type = this.typeLabel(content?.type, 'user-service');
    }

    if (content instanceof Collection) {
      this.contentData.lessons = content?.stats?.total;
    }

    this.detectChanges();
  }

  protected loadProfile() {
    if (!this.models.teacher) {
      return;
    }
    if (this.teacherProfile) {
      return;
    }
    if (!this.models.content) {
      this.prepareUserCard(this.models.teacher);
    }
    if (this.models.teacher.profile) {
      this.teacherProfile = this.models.teacher.profile;
    } else {
      this.profileService
        .getProfile(this.models.teacher.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (profile) => {
            this.teacherProfile = profile;
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }
  }

  public trackByFn(index: number, item: SingleEvent): string {
    return item.id;
  }

  public onCourseSelected(service: EventsSequenceExtended) {
    const { user_service_contract_id } = service;
    const request = new SignupUserServiceContractRequest({
      user_service_contract_id,
      currency_id: this._me.currency_id,
    });
    this.userServiceContractsService
      .signup(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.onServiceSigned(response.model, response.contract, response.contractor);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected suggestLicense() {
    if (this.typeOfContent !== 'collection') {
      return;
    }
    const price_tiers = this.contentWithType['price_tiers'] || {};
    let licenseOrder: CollectionLicenseEnum[];
    const isTeacher = AccountService.isAsIfTeacher(this.me);
    const isSchoolOwner = AccountService.hasSegment(this.me, UserSegmentEnum.school_owner);
    if (isSchoolOwner) {
      licenseOrder = [
        CollectionLicenseEnum.business,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
      ];
    } else if (isTeacher) {
      licenseOrder = [
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.business,
      ];
    } else {
      /** @todo Если здесь одно и то же, зачем ещё одна ветка? */
      licenseOrder = [
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.business,
      ];
    }
    const betterLicense = licenseOrder.find((c) => c in price_tiers && price_tiers[c] > 0);

    if (!this.selectedLicense && !!this.purchasedLicense) {
      this.selectedLicense = this.purchasedLicense;
    }

    if (!this.selectedLicense && this.me && this.models.content) {
      this.selectedLicense = betterLicense;
    }
    this.detectChanges();
  }

  private initScheduleRefresh(user_id: string, old_user_id?: string) {
    if (old_user_id) {
      this.websocketService.stopWatching('schedule', old_user_id);
    }
    if (user_id) {
      this.websocketService.startWatching('schedule', user_id);
    }
  }

  /** ****************** **/

  /** UTILS **/

  public screenClasses() {
    const isPortrait = this.screenService.deviceOrientation === DeviceOrientationEnum.portrait;
    return {
      tablet: this.deviceService.isTablet(),
      mobile: this.deviceService.isMobile(),
      desktop: (!this.deviceService.isTablet() && !this.deviceService.isMobile()) || this.deviceService.isDesktop(),
      portrait: !this.deviceService.isDesktop() && isPortrait,
      landscape: !this.deviceService.isDesktop() && !isPortrait,
    };
  }

  public get avatar() {
    return {
      img: this.authorData?.userPic,
      title: this.authorData?.fullName || '',
    };
  }

  public get cover() {
    return {
      img: this.contentData?.coverUrl,
      title: this.contentData?.title,
    };
  }

  public dateStringToDate(date: string | Date): Date {
    return new Date(date);
  }

  public coverCssClasses(count: number, index: number): any {
    switch (count) {
      case 1:
        return { narrow: false, short: false };
      case 2:
        return { narrow: false, short: true };
      case 3:
        return index === 0 ? { narrow: false, short: true } : { narrow: true, short: true };
      case 4:
        return { narrow: true, short: true };
      default:
        return { narrow: false, short: false };
    }
  }

  public coverCollection(cover: string): OnUiCover {
    return {
      img: cover,
      form: 'base',
      type: 'cover',
    };
  }

  public typeLabel(type: EUserServiceType | MeetingTypeEnum, contentType: PaymentCheckoutContentType): string {
    if (contentType === 'user-service') {
      return `library.user-service-card.type.${EUserServiceType[type]}`;
    } else if (contentType === 'meeting') {
      return MeetingTypeEnum[type];
    }
  }

  public getPreparedContent(type: PaymentCheckoutContentType) {
    switch (type) {
      case 'meeting':
        return this.models.content as Meeting;
      case 'user-service':
        return this.models.content as UserService;
      case 'collection':
        return this.models.content as Collection;
    }
  }

  public onToggleContentInModal(content?: UserService) {
    this.userContentFullInModal.emit(content);
  }

  public resetForm() {
    this.models.resetCalendar();
    this.models.reset();
  }

  public selectTeacher() {
    this.models.goNextStep('teacher');
  }

  public selectContent() {
    this.models.goNextStep('detail');
  }

  public get isOngoingService(): boolean {
    if (this.typeOfContent === 'user-service') {
      return (this.models.content as UserService)?.is?.ongoing;
    }
    return false;
  }

  public get isSchedulableService(): boolean {
    if (this.typeOfContent === 'user-service') {
      return (this.models.content as UserService)?.is?.schedulable;
    }
    return false;
  }

  public get typeOfContent() {
    return this.models.contentType(this.models.content);
  }

  public get contentWithType() {
    return this.getPreparedContent(this.typeOfContent);
  }

  public getExchangeRate(currency_id: number) {
    this.billingService
      .getExchangeRate(this.myCurrency, currency_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (exchange) => {
          this.exchangeRate = exchange;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public agreementChanged(event: boolean) {
    this.isAgreementError = this.showAgreementError = !event;
  }

  public get purchasedLicense(): CollectionLicenseEnum {
    if (this.typeOfContent === 'collection') {
      if ((this.models.content as Collection).visit_info.licensed) {
        this.selectedLicense = (this.models.content as Collection).visit_info.licensed;
        return (this.models.content as Collection).visit_info.licensed;
      }
    }
    return null;
  }

  public get isMe(): boolean {
    return this._me && this.models.teacher && this._me?.id === this.models.teacher.id;
  }

  public get showFooter() {
    if (this.models.content) {
      if (this.typeOfContent !== 'collection') {
        return true;
      } else {
        return !this.models.isCurrentStep('detail');
      }
    } else {
      return false;
    }
  }

  public trackContentByFn(index: number, content: Meeting | UserService | Collection) {
    return content.id;
  }

  public isCurrentTeacherSelected(teacher: User): boolean {
    return this.models.teacher?.id === teacher.id;
  }

  public onTeacherSelected(teacher: User) {
    this.models.teacher = teacher;
    this.stateChanged.emit(this.models.step);
    this.loadProfile();
    this.initScheduleRefresh(this.models.teacher?.id);
    this.detectChanges();
  }

  public timeSelected() {
    this.stateChanged.emit(this.models.step);
  }

  /** ****************** **/

  /** CARDS **/

  protected loadUserCards(update = false) {
    this.billingService
      .getCards()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (cards) => {
          this.cardsState = cards.length > 0;
          if (!update) {
            this.models.init({
              cards,
            });
          }
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.cardsState = false;
        },
      });
  }

  public newCardComplete() {
    this.loadUserCards();
  }

  public onBillingCardSelected(card) {
    if (!this.models.content?.id) {
      return;
    }

    /** Не пересоздавать транзакцию */
    if (this._billingOperation && this._billingCard === card) {
      return;
    }

    this._billingCard = card;
    // this.paymentFormValid = !!card;
    this.receipt = null;

    switch (this.typeOfContent) {
      case 'meeting': {
        this.billingService
          .purchaseMeeting(this.models.content.id, this._billingCard?.id)
          .pipe(first(), takeUntil(this.destroyed$))
          .subscribe({
            next: (res) => {
              this._billingOperation = res;
              this.receipt = new CReceipt(res.receipt);
              this.totalFormatted = this.receipt.total.formatFixed();
              let contentCurrency = this.contentWithType['price_tier'];
              if (this.contentWithType['currency_id']) {
                contentCurrency = this.contentWithType['currency_id'];
              }
              this.getExchangeRate(contentCurrency);
              this.detectChanges();
            },
            error: (error) => {
              this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            },
          });
        break;
      }

      case 'collection': {
        /*
        this.collectionsService
          .purchase(this.models.content.id, this.selectedLicense)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: (purchaseOperation) => {
              this._billingOperation = purchaseOperation;
              this.receipt = new CReceipt(purchaseOperation.receipt);
              this.totalFormatted = this.receipt.total.formatFixed();
              this.detectChanges();
            },
            error: (error) => {
              this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
            },
          });
          */
        break;
      }

      case 'user-service': {
        // alert('Not implemented');
        return;
      }
    }
  }

  /** ****************** **/

  /** ACTIONS **/

  public executePayment() {
    if (this.payButtonsState !== 'default') {
      return;
    }
    this.paymentWidgetService.pay();
    this.detectChanges();
  }

  public onPaymentSucceed(operationId?: string) {
    this.succeed.emit(true);
    this.paymentProcessing = false;
    this.payButtonsState = 'done';
    this.resetPayButtonState();
    this.stateChanged.emit('payment');
    this.models.goDone();
    this.detectChanges();
  }

  public onPaymentFailed(res: BillingOperation) {
    // не оплачено
    this.paymentFormValid = false;
    this.paymentProcessing = false;
    this.paymentProcessingError = res?.details?.error;
    this.payButtonsState = 'fail';
    this.stateChanged.emit('payment');
    this.resetPayButtonState();
    this.models.goFail();
  }

  public onPaymentProcessing(running?: boolean) {
    if (running) {
      this.paymentProcessing = true;
      this.payButtonsState = 'progress';
      this.paymentProcessingError = '';
    }
  }

  protected resetPayButtonState(timeout = 1500) {
    this.clearTimeout(this._resetPayButtonState);
    this._resetPayButtonState = this.setTimeout(() => {
      this.payButtonsState = 'default';
    }, timeout);
  }

  /**
   * Запись на занятие к преподавателю
   */
  public signupToService() {
    if (this.signupProcessing) {
      return;
    }

    if (this.isMe) {
      this.isOwnServiceError = true;
      return;
    }

    const service = this.models.content as UserService;
    const teacher = this.models.teacher;
    const events: ISignupEvent[] = this.models.calendar.time.map((time) => ({
      begin_at: time,
      end_at: addMinutes(time, service.duration),
    }));

    let signup$: Observable<SignupUserServiceContractResponse>;
    if (service?.is?.onetime) {
      signup$ = this.signupToOnetimeService$(service, teacher, events);
    } else if (service?.is?.ongoing) {
      signup$ = this.signupToOngoingService$(service, teacher, events);
    } else {
      return;
    }

    this.signupProcessing = true;
    this.signupProcessingError = '';
    signup$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response) => {
        this.signupProcessing = false;
        this.onServiceSigned(response.model, response.contract, response.contractor);
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'PAYMENT-PROBLEM');
        this.toastr.error(error.message, 'Service booking');
        captureException(error);
        this.signupProcessing = false;
        this.signupProcessingError = error;
      },
    });
  }

  public setSelectedLicense(license: CollectionLicenseEnum) {
    this.selectedLicense = license;
    this.setupPaymentWidget();
    this.models.goNextStep();
    this.detectChanges();
  }

  /** Настройка данных для проведения платежа */
  public setupPaymentWidget() {
    // if (this._isCheckout || this._checkoutDetails) {
    if (this._isCheckout) {
      return;
    }

    this._isCheckout = true;
    const model = this.getPreparedContent('collection');
    const license = this.selectedLicense;

    zip(this.collectionsService.purchase(model.id, license), this.store.select(getProfileSettings))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([details, settings]) => {
          this._isCheckout = false;
          // this._checkoutDetails = details;
          this._userSettings = settings;

          /*
          if (!this._checkoutDetails.status) {
            logger.error(this._checkoutDetails);
            this.errorMessage = this._checkoutDetails.error;
            this.activateView('error');
            return;
          }
          */
          // const options = this._checkoutDetails.model;
          const order_id = String(details.billing_order_id);
          const charge_id = String(details.billing_charge_id);

          this.paymentOptions = {
            first_name: this._me.first_name,
            last_name: this._me.last_name,

            email: this._userSettings.email,
            phone: this._userSettings.mobile_phone,

            country: this._me.country?.alpha2Code,

            order_id,
            charge_id: [charge_id],
            meta: {
              collection_id: model.id,
              account_id: this._me?.id,
            },
          };

          // this.totalFormatted = this.receipt.total.formatFixed();

          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.errorMessage = error.error.message;
          this.detectChanges();
          // this.activateView('error');
        },
      });
  }

  public onPaymentConfigured(configuration: PaymentConfiguration) {
    this.paymentConfigured = configuration.complete;
    this.detectChanges();
  }

  public onStartPayment() {
    this.payClicked = true;
    // super.onStartPayment();
    this.detectChanges();
  }

  public onAddAccount(providerId: string) {
    this.openAddAccountModal = true;
    this.addAccountProviderId = providerId;
  }

  public onCloseAddAccount() {
    this.openAddAccountModal = false;
    this.afterAddAccountNeedUpdate = Date.now();
    this.detectChanges();
  }

  protected get timezone(): string {
    return /* this.regularSchedule?.timezone || */ this._me?.timezone || this.clientTimezone;
  }

  protected get clientTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return 'UTC';
    }
  }

  protected onServiceSigned(
    order: UserServiceContractOrder,
    contract: UserServiceContract,
    contractor: UserServiceContractor,
  ) {
    this.userServiceContractOrder = order;
    this.userServiceContract = contract;
    this.userServiceContractor = contractor;

    // переход к оплате
    this.models.goNextStep();
    this.detectChanges();

    this.watchForContractOrderPaid(order);
  }

  protected watchForContractOrderPaid(order: UserServiceContractOrder) {}

  protected signupToOnetimeService$(
    service: UserService,
    teacher: User,
    events: ISignupEvent[],
  ): Observable<SignupUserServiceContractResponse> {
    const request = new CreateUserServiceContractRequest({
      user_service_id: service.id,
      teacher_id: teacher.id,
      account_id: this._me?.id,
      schedule: {
        interval: UserServiceContractIntervalEnum.none,
        begin_at: null, // auto = min of events
        end_at: null, // not used
        total: null, // auto = service.options.meetings_limit,
        events,
      },
      timezone: this.timezone,
      currency_id: this._me.currency_id,
    });
    return this.userServiceContractsService.createAndSignup(request);
  }

  /** регулярные занятия */
  protected signupToOngoingService$(
    service: UserService,
    teacher: User,
    events: ISignupEvent[],
  ): Observable<SignupUserServiceContractResponse> {
    const request = new CreateUserServiceContractRequest({
      user_service_id: service.id,
      teacher_id: teacher.id,
      account_id: this._me?.id,
      schedule: {
        interval: UserServiceContractIntervalEnum.weekly,
        begin_at: null, // auto = min of events
        end_at: null, // endless
        total: null, // endless
        events,
      },
      timezone: this.timezone,
      currency_id: this._me.currency_id,
    });
    return this.userServiceContractsService.createAndSignup(request);
  }

  /** Обновление расписания при реконнекте к веб-сокетам */
  protected get watchWsReconnected$() {
    return this.websocketService.status$.pipe(
      debounceTime(1000),
      tap((isOnline) => {
        if (isOnline) {
          logger.debug('PaymentCheckoutWizardComponent -> watchWsReconnected$ -> online -> refreshSchedule()');
          this.refreshSchedule();
        }
      }),
    );
  }

  /**
   * Обновление расписания при уведомлении об измении преподавателя или пользователя
   *
   * @see initScheduleRefresh
   */
  protected get watchScheduleUpdated$() {
    return this.websocketService.on<IEntityUpdate>(WebEvent.Update).pipe(
      filter(
        (update) =>
          update.entity === 'schedule' && (update.id === this._me?.id || update.id === this.models?.teacher?.id),
      ),
      debounceTime(1000),
      tap(() => {
        logger.debug('PaymentCheckoutWizardComponent -> onScheduleUpdate() -> refreshSchedule()');
        this.refreshSchedule();
      }),
    );
  }

  protected refreshSchedule() {
    if (this.isSchedulableService && !this.isMe) {
      this.loadServiceSignupSchedule();
    }
  }

  /** Загрузка расписания для услуг, на которые время заранее резервируется преподавателем */
  private loadServiceSignupSchedule() {
    this.loadingCourses = true;
    const request: UserServiceScheduleRequest = {
      date_from: startOfWeek(new Date()),
      date_to: addMonths(startOfWeek(new Date()), 3),
      user_service_id: this.models?.content?.id,
      teacher_id: this.models?.teacher?.id || this.models?.content?.author_id,
      student_id: this.me.id,
    };
    this.scheduleService
      .findServiceSignupSchedule(request)
      .pipe(
        switchMap((response: UserServiceScheduleResponse) =>
          this.eventsSequenceExtendedService.extend$(response.service_events, ['meeting']),
        ),
        tap(),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (service_events: EventsSequence[]) => {
          this.loadingCourses = false;
          this.courses = service_events;
          this.courses.sort((a, b) => a.begin_at.getTime() - b.begin_at.getTime());
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loadingCourses = false;
          this.detectChanges();
        },
      });
  }

  public get isUserService() {
    return this.typeOfContent === 'user-service';
  }

  public calcMeetDate(date) {
    /**
     * @TODO: Не знаю что показать если будет выбраны 2 и более дат
     */
    return date[0];
  }

  public calcMeetTime(time) {
    const startTime = this.formatPipe.transform(time[0], 'HH:mm');
    let endTime = null;
    if (this.typeOfContent === 'user-service') {
      endTime = this.formatPipe.transform(
        addMinutes(new Date(time[0]), (this.models.content as UserService).duration),
        'HH:mm',
      );
    }
    return startTime + (endTime ? ` - ${endTime}` : '');
  }
}
